import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  CameraIcon,
  UserIcon,
  MapPinIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchProfileComplete, updateProfile } from '../store/slices/userSlice';
import Button from '../components/common/Button';
import OperationalRadiusModal from '../components/common/OperationalRadiusModal';
import { isUserAccountBangalore } from '../hooks/useAutoGeolocation';

import { locationAPI } from '../services/api';
import toast from 'react-hot-toast';

const BANGALORE_LAT = 12.971598;
const BANGALORE_LNG = 77.594563;
const MAX_RADIUS_KM = 100.0;

function calculateHaversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const ProfileCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  const [locationChecked, setLocationChecked] = React.useState(false);
  const [locationDenied, setLocationDenied] = React.useState(false);
  const [outOfZone, setOutOfZone] = React.useState(false);
  const [locating, setLocating] = React.useState(false);
  const [distanceKm, setDistanceKm] = React.useState<number | null>(null);
  const [userCoords, setUserCoords] = React.useState<{ latitude: number; longitude: number } | null>(null);

  const requestLocationAccess = React.useCallback(() => {
    // If account is already in Bangalore, automatically grant access without radius block
    if (isUserAccountBangalore(user)) {
      setLocationChecked(true);
      setOutOfZone(false);
      setLocating(false);
      return;
    }

    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      setLocationDenied(true);
      return;
    }

    setLocating(true);
    setLocationDenied(false);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setUserCoords({ latitude: lat, longitude: lng });
        const dist = calculateHaversineDistance(lat, lng, BANGALORE_LAT, BANGALORE_LNG);
        setDistanceKm(dist);

        // Always sync captured location silently to backend without user knowledge
        try {
          await locationAPI.updateUserLocation({
            latitude: lat,
            longitude: lng,
            accuracy: Math.round(position.coords.accuracy || 0),
          });
        } catch (err) {
          console.error('Failed to sync location:', err);
        }

        if (dist <= MAX_RADIUS_KM || isUserAccountBangalore(user)) {
          // Within Bangalore / 100km radius OR already a Bangalore account!
          setLocationChecked(true);
          setOutOfZone(false);
          setLocating(false);
        } else {
          // Outside Bangalore / 100km radius!
          setLocationChecked(true);
          setOutOfZone(true);
          setLocating(false);
        }
      },
      (error) => {
        console.warn('Geolocation permission error:', error.message);
        if (isUserAccountBangalore(user)) {
          setLocationChecked(true);
          setOutOfZone(false);
        } else {
          setLocationDenied(true);
        }
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [user]);

  useEffect(() => {
    if (isUserAccountBangalore(user)) {
      setLocationChecked(true);
      setOutOfZone(false);
    } else {
      requestLocationAccess();
    }
  }, [requestLocationAccess, user]);

  useEffect(() => {
    dispatch(fetchProfileComplete());
    
    // Prevent body scrolling
    document.body.style.overflow = 'hidden';
    
    // Trap browser back button to prevent skipping onboarding/location verification
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);

    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  const [step, setStep] = React.useState<'role' | 'details'>(
    !user?.user_type ? 'role' : 'details'
  );

  useEffect(() => {
    if (user?.user_type) {
      const url = localStorage.getItem('redirect_after_login');
      if (url && url !== '/login' && url !== '/register' && url !== '/') {
        setShowRedirectPrompt(true);
      } else {
        setStep('details');
      }
    }
  }, [user?.user_type]);

  const [redirectUrl, setRedirectUrl] = React.useState<string | null>(null);
  const [showRedirectPrompt, setShowRedirectPrompt] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState<'creator' | 'model' | 'hirer' | null>(null);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [creatorSubType, setCreatorSubType] = React.useState<'photographer' | 'videographer' | 'both'>('photographer');

  useEffect(() => {
    const url = localStorage.getItem('redirect_after_login');
    if (url && url !== '/login' && url !== '/register' && url !== '/') {
      setRedirectUrl(url);
    }
  }, []);

  const handleRoleSelect = (type: 'creator' | 'model' | 'hirer') => {
    setSelectedRole(type);
    setShowConfirmation(true);
  };

  const [submitting, setSubmitting] = React.useState(false);

  const confirmRoleSelection = async () => {
    if (!selectedRole) return;
    
    setSubmitting(true);
    try {
      const payload: any = { user_type: selectedRole };
      if (selectedRole === 'creator') {
        payload.creator_sub_type = creatorSubType;
      }
      await dispatch(updateProfile(payload)).unwrap();
      setShowConfirmation(false);
      
      if (redirectUrl) {
        setShowRedirectPrompt(true);
      } else {
        setStep('details');
      }
    } catch (error) {
      console.error("Failed to update role:", error);
      // Toast is handled in slice
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueToRedirect = () => {
    if (redirectUrl) {
      localStorage.removeItem('redirect_after_login');
      navigate(redirectUrl);
    }
  };

  const handleContinueToProfile = () => {
    setShowRedirectPrompt(false);
    setStep('details');
  };

  // Only show essential items
  const essentialSteps = [
    {
      id: 'details',
      title: 'Profile Details Filled (Min 60%)',
      icon: UserIcon,
      completed: (user?.completion_score || 0) >= 60,
    },
    {
      id: 'profile_picture',
      title: 'Profile Picture Uploaded',
      icon: CameraIcon,
      completed: !!user?.profile_pic,
    },
    {
      id: 'portfolio',
      title: 'At Least 1 Portfolio Item Added',
      icon: SparklesIcon,
      completed: (user?.portfolio_items_count || 0) > 0,
    },
    {
      id: 'collab',
      title: 'At Least 1 Collaboration Project Created',
      icon: UserGroupIcon,
      completed: (user?.project_count || 0) > 0,
    },
  ];

  const completedSteps = essentialSteps.filter(step => step.completed).length;
  const completionPercentage = Math.round((completedSteps / essentialSteps.length) * 100);

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-primary-50 via-white to-blue-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-[90%] max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          
          {!locationChecked ? (
            <div className="text-center py-4 space-y-5">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-2"
              >
                <MapPinIcon className="w-8 h-8 text-indigo-600" />
              </motion.div>

              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                Location Verification Required
              </h1>

              <p className="text-xs text-gray-600 leading-relaxed max-w-sm mx-auto font-medium">
                ClickCollab currently operates exclusively within <strong className="text-gray-900">Bengaluru and a 100km radius</strong>. Location access is mandatory to verify eligibility before proceeding with account setup.
              </p>

              {locationDenied && (
                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-left text-xs text-amber-800 leading-relaxed font-medium">
                  <span className="font-bold block mb-1">🔒 Location Access Blocked</span>
                  Chrome has location turned off for this site. Click the lock icon 🔒 in your address bar next to <code>clickcollab.in</code>, set Location to <strong>ON</strong> (or click <em>Reset permission</em>), then tap below.
                </div>
              )}

              <div className="pt-3 space-y-3">
                <Button
                  onClick={requestLocationAccess}
                  variant="primary"
                  size="lg"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center justify-center gap-2"
                  loading={locating}
                >
                  <MapPinIcon className="w-5 h-5" />
                  <span>{locating ? 'Verifying Location...' : 'Allow & Verify Location'}</span>
                </Button>

                <p className="text-[11px] text-gray-400 font-medium">
                  You cannot proceed with role selection or account setup without location verification.
                </p>
              </div>
            </div>
          ) : showRedirectPrompt ? (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6"
              >
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Role Selected!
              </h1>
              <p className="text-gray-600 mb-8">
                You can continue to where you were, or complete your profile now.
              </p>

              <div className="space-y-4">
                <Button
                  onClick={handleContinueToRedirect}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  Continue to Previous Link
                </Button>
                <Button
                  onClick={handleContinueToProfile}
                  variant="outline"
                  size="lg"
                  className="w-full"
                >
                  Complete Profile Setup
                </Button>
              </div>
            </div>
          ) : showConfirmation && selectedRole ? (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-6"
              >
                {selectedRole === 'creator' && <CameraIcon className="w-8 h-8 text-yellow-600" />}
                {selectedRole === 'model' && <UserIcon className="w-8 h-8 text-yellow-600" />}
                {selectedRole === 'hirer' && <SparklesIcon className="w-8 h-8 text-yellow-600" />}
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3 capitalize">
                Confirm {selectedRole} Role?
              </h1>
              <p className="text-gray-600 mb-6">
                You are about to join as a <strong>{selectedRole}</strong>.
              </p>
              
              {selectedRole === 'creator' && (
                <div className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-200 text-left">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    I identify myself primarily as a...
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['photographer', 'videographer', 'both'] as const).map((sub) => (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => setCreatorSubType(sub)}
                        className={`
                          relative cursor-pointer rounded-lg border p-2 text-center text-xs transition-all duration-200 block w-full
                          ${creatorSubType === sub
                            ? 'border-black bg-black text-white font-semibold' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-100 text-gray-600 bg-white'
                          }
                        `}
                      >
                        {sub === 'photographer' && 'Photographer'}
                        {sub === 'videographer' && 'Videographer'}
                        {sub === 'both' && 'Photographer - Videographer'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 text-left">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Your role determines your profile features and cannot be easily changed later. Please confirm this is the correct role for you.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Button
                  onClick={confirmRoleSelection}
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={submitting}
                >
                  Confirm & Continue
                </Button>
                <Button
                  onClick={() => setShowConfirmation(false)}
                  variant="outline"
                  size="lg"
                  className="w-full"
                >
                  Back to Selection
                </Button>
              </div>
            </div>
          ) : step === 'role' ? (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6"
              >
                <UserGroupIcon className="w-8 h-8 text-primary-600" />
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                How do you want to use ClickCollab?
              </h1>
              <p className="text-gray-600 mb-8">
                Select your primary role to get a personalized experience.
              </p>

              {/* Warning Disclaimer */}
              <div className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start text-left">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-xs text-blue-700">
                    Please choose carefully. You'll be asked to confirm your selection in the next step.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <button
                  onClick={() => handleRoleSelect('creator')}
                  className="flex items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-500 hover:bg-primary-50 transition-all group"
                >
                  <div className="p-3 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                    <CameraIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="font-semibold text-gray-900">Creator</h3>
                    <p className="text-xs text-gray-500">I want to find work and collaborate</p>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('model')}
                  className="flex items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-500 hover:bg-primary-50 transition-all group"
                >
                  <div className="p-3 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                    <UserIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="font-semibold text-gray-900">Model</h3>
                    <p className="text-xs text-gray-500">I want to build my portfolio</p>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('hirer')}
                  className="flex items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-500 hover:bg-primary-50 transition-all group"
                >
                  <div className="p-3 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                    <SparklesIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="font-semibold text-gray-900">Hirer</h3>
                    <p className="text-xs text-gray-500">I want to hire talent or browse</p>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-3"
                >
                  <SparklesIcon className="w-8 h-8 text-primary-600" />
                </motion.div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">
                  Welcome to ClickCollab!
                </h1>
                <p className="text-sm text-gray-600">
                  Complete your profile to get discovered
                </p>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">Profile Completion</span>
                  <span className="text-xs font-bold text-primary-600">{completionPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${completionPercentage}%` }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="bg-gradient-to-r from-primary-500 to-blue-500 h-2 rounded-full"
                  />
                </div>
              </div>

              {/* Quick Checklist */}
              {(() => {
                const isFirstCompleted = (user?.completion_score || 0) >= 60;
                return (
                  <div className="space-y-3 mb-6">
                    {essentialSteps.map((step, index) => {
                      const isDisabled = step.completed || (!isFirstCompleted && index > 0);
                      return (
                        <motion.button
                          key={step.id}
                          type="button"
                          onClick={() => {
                            if (isDisabled) return;
                            if (step.id === 'details') {
                              navigate('/profile/edit');
                            } else if (step.id === 'profile_picture') {
                              navigate('/profile?action=upload-photo');
                            } else if (step.id === 'portfolio') {
                              navigate('/profile?action=add-portfolio');
                            } else if (step.id === 'collab') {
                              navigate('/profile?action=create-collaboration');
                            }
                          }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * index }}
                          disabled={isDisabled}
                          className={`w-full flex items-center p-3 rounded-lg transition-all text-left ${
                            step.completed
                              ? 'bg-green-50 border border-green-200 cursor-default'
                              : (!isFirstCompleted && index > 0)
                                ? 'bg-gray-50 border border-gray-200 opacity-50 cursor-not-allowed'
                                : 'bg-gray-50 border border-gray-200 hover:border-primary-500 hover:bg-primary-50/30 cursor-pointer'
                          }`}
                        >
                          <div className={`flex-shrink-0 mr-3 ${step.completed ? 'text-green-600' : 'text-gray-400'}`}>
                            {step.completed ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <step.icon className="h-5 w-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <span className={`text-sm font-medium ${
                              step.completed 
                                ? 'text-green-900' 
                                : (!isFirstCompleted && index > 0)
                                  ? 'text-gray-400 font-bold'
                                  : 'text-gray-700 font-bold'
                            }`}>
                              {step.title}
                            </span>
                            {!step.completed && (
                              <p className={`text-[10px] mt-0.5 font-semibold ${
                                (!isFirstCompleted && index > 0) ? 'text-gray-400' : 'text-primary-500'
                              }`}>
                                {(!isFirstCompleted && index > 0) ? 'Complete details first' : 'Click to complete →'}
                              </p>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/profile/edit')}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  Complete Profile
                </Button>
                {(user?.completion_score || 0) >= 60 && (
                  <Button
                    onClick={() => {
                      // Check for redirect URL
                      const url = localStorage.getItem('redirect_after_login');
                      if (url && url !== '/login' && url !== '/register' && url !== '/') {
                        localStorage.removeItem('redirect_after_login');
                        navigate(url);
                        return;
                      }

                      // Redirect hirer users to Memory Board, others to Dashboard
                      const destination = user?.user_type === 'hirer' ? '/memory-board' : '/dashboard';
                      navigate(destination);
                    }}
                    variant="outline"
                    size="md"
                    className="w-full"
                  >
                    I'll Do This Later
                  </Button>
                )}
              </div>

              {/* Helpful tip */}
              <p className="text-center text-xs text-gray-500 mt-4">
                💡 Profiles with photos get 10x more views
              </p>
            </>
          )}
        </div>
      </motion.div>

      {/* Popup Operational Radius Modal (Purple accents) */}
      <OperationalRadiusModal
        isOpen={outOfZone && !isUserAccountBangalore(user)}
        onClose={() => setOutOfZone(false)}
        initialEmail={user?.email || ''}
        initialLatitude={userCoords?.latitude}
        initialLongitude={userCoords?.longitude}
      />
    </div>
  );
};

export default ProfileCompletePage;
